import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { ApiUsagePricing, ApiPricing, OrgApiDailyUsage, ApiUsageItems, User } from '@app/models';
import { AppService, ErrorHandlerService, PaymentService } from '@app/services';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';
import { LoaderService } from '@app/shared';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { LdatePipe } from '@app/shared/pipes';
import { CurrencyPipe } from '@angular/common';
import { I18nService } from '@app/core';
import { ApiUsageLimitsModalService } from '../api-usage-limits-modal/api-usage-limits-modal.service';

dayjs.extend(utc);

export interface ChartSeriesItem {
  name: string;
  value: number;
  cost: number;
  usage: number;
}

export interface TransformedChartItem {
  id: string;
  name: string;
  totalUsage: number;
  totalCost: number;
  series: ChartSeriesItem[];
  lastNameGrouped?: string; // se existir agrupamento aqui fica salva o ultimo nome(data) do grupo
}

export enum ApiUsagePriority {
  Email = 0,
  SMS = 1,
  WhatsApp = 2,
  WhatsAppOTP = 3,
  SignPhoneSecurityVerification = 4,
  CreateDocument = 5,
  DocumentsQuery = 6,
  Webhook = 7
}

export const ApiUsagePriorityColors: Record<ApiUsagePriority, string> = {
  [ApiUsagePriority.Email]: '',
  [ApiUsagePriority.SMS]: '',
  [ApiUsagePriority.WhatsApp]: '',
  [ApiUsagePriority.WhatsAppOTP]: '',
  [ApiUsagePriority.SignPhoneSecurityVerification]: '',
  [ApiUsagePriority.CreateDocument]: '',
  [ApiUsagePriority.DocumentsQuery]: '',
  [ApiUsagePriority.Webhook]: ''
};

const ApiUsagePriorityMap: Record<string, ApiUsagePriority> = {
  email: ApiUsagePriority.Email,
  sms: ApiUsagePriority.SMS,
  whatsapp: ApiUsagePriority.WhatsApp,
  whatsapp_otp: ApiUsagePriority.WhatsAppOTP,
  sign_phone_security_verification: ApiUsagePriority.SignPhoneSecurityVerification,
  create_document: ApiUsagePriority.CreateDocument,
  documents_query: ApiUsagePriority.DocumentsQuery,
  webhook: ApiUsagePriority.Webhook
};

@Component({
  selector: 'app-api-usage',
  templateUrl: './api-usage.component.html',
  styleUrls: ['./api-usage.component.scss'],
  standalone: false
})
export class ApiUsageComponent implements OnInit {
  @Input() currency = 'BRL';
  @Input() currencyCode = 'R$';
  @Input() isMobile: boolean;
  @Input() currentUser: User;
  isLoading = false;
  resultsLineChartData: TransformedChartItem[] = [];
  isShowingCost = false;
  summary: {
    totalUsage: number;
    totalCost: number;
    seriesSummary: { name: string; totalValue: number; totalCost: number }[];
  } = {
    totalUsage: 0,
    totalCost: 0,
    seriesSummary: []
  };

  // options chart
  view = [1076, 400];
  colorScheme: { domain: any[] } = {
    domain: []
  };
  tickCounter = 0;

  selectedDateRange: { startDate: dayjs.Dayjs; endDate: dayjs.Dayjs };
  dateRangePickerOptions: any;
  @ViewChild('dateRangePickerLabel') dateRangePickerLabelRef: ElementRef;

  readonly ApiUsagePriority = ApiUsagePriority;
  readonly COST_THRESHOLD_BRL = 99;
  readonly COST_THRESHOLD_USD = 19;

  constructor(
    public translateService: TranslateService,
    private errorHandlerService: ErrorHandlerService,
    private paymentService: PaymentService,
    private loaderService: LoaderService,
    private appService: AppService,
    private lDatePipe: LdatePipe,
    private currencyPipe: CurrencyPipe,
    private i18nService: I18nService,
    private apiUsageLimitsModalService: ApiUsageLimitsModalService
  ) {}

  ngOnInit(): void {
    this.dateRangePickerOptions = this.appService.getDateRangePickerOptions();
    this.selectedDateRange = {
      startDate: dayjs().startOf('month'),
      endDate: dayjs()
    };
    this.setColorScheme();
    this.organizationApiUsage();
  }

  setColorScheme() {
    const rootStyles = getComputedStyle(document.documentElement);
    const colors = [
      rootStyles.getPropertyValue('--clr-primary-500').trim() || '#2889ff',
      rootStyles.getPropertyValue('--clr-premium-500').trim() || '#9659f9',
      rootStyles.getPropertyValue('--clr-success-500').trim() || '#00c4a2',
      rootStyles.getPropertyValue('--clr-success-600').trim() || '#00a48c',
      rootStyles.getPropertyValue('--clr-premium-400').trim() || '#af89fc',
      rootStyles.getPropertyValue('--clr-autentigrey-700').trim() || '#6d7188',
      rootStyles.getPropertyValue('--clr-autentigrey-500').trim() || '#8b92a6',
      rootStyles.getPropertyValue('--clr-autentigrey-300').trim() || '#b3bac6'
    ];
    this.colorScheme = { domain: colors };

    Object.keys(ApiUsagePriority)
      .filter(key => isNaN(Number(key)))
      .forEach((key, index) => {
        ApiUsagePriorityColors[ApiUsagePriority[key as keyof typeof ApiUsagePriority]] = colors[index] || '#000000';
      });
  }

  getColorByCategory(category: string): string {
    const priority = ApiUsagePriorityMap[category];
    return ApiUsagePriorityColors[priority] || '#000000';
  }

  allowApplyOnlyWhenStartAndEndDateExists(bool: boolean) {
    if (this.isLoading && !this.dateRangePickerOptions) return;
    this.dateRangePickerLabelRef.nativeElement.querySelector('ngx-daterangepicker-material .buttons .btn:last-of-type').disabled = !bool;
  }

  openApiUsageLimitsModal() {
    this.apiUsageLimitsModalService.open({ currency: this.currency }).subscribe();
  }

  organizationApiUsage() {
    if (this.isLoading) return;
    this.isLoading = true;
    this.loaderService.show();

    const startDate = this.selectedDateRange.startDate.format('YYYY-MM-DD');
    const endDate = this.selectedDateRange.endDate.format('YYYY-MM-DD');
    this.paymentService
      .organizationApiUsage({ start_date: startDate, end_date: endDate })
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: apiUsage => {
          const transformedData = this.fillMissingDatesOrCreateZeroData(this.transformApiData(apiUsage), this.selectedDateRange.startDate, this.selectedDateRange.endDate);

          this.resultsLineChartData = this.groupChartData(transformedData, this.selectedDateRange.startDate, this.selectedDateRange.endDate);
          this.calculateSummary(this.resultsLineChartData);
        },
        error: error => this.errorHandlerService.handle(error)
      });
  }

  transformApiData(apiData: ApiUsagePricing): TransformedChartItem[] {
    const pricingList: ApiPricing[] = apiData.pricing || [];
    const usageList: OrgApiDailyUsage[] = apiData.usage || [];

    // Filtrar os preços corretos pela moeda
    const filteredPricing = pricingList.filter(p => p.currency.toUpperCase() === this.currency.toUpperCase() && p.valid_since);
    const sortedPricing = filteredPricing.sort((a, b) => new Date(a.valid_since as string).getTime() - new Date(b.valid_since as string).getTime());

    function getPricingForDay(dayString: string): ApiPricing | null {
      const dayTime = new Date(dayString).getTime();
      const validPricings = sortedPricing.filter(p => new Date(p.valid_since as string).getTime() <= dayTime);
      return validPricings.length > 0 ? validPricings[validPricings.length - 1] : null;
    }

    return usageList
      .map(u => {
        const dayStr = (u.day as string).slice(0, 10);
        const pricingForDay = getPricingForDay(u.day as string);
        if (!pricingForDay) return null;

        const dailyItems: ApiUsageItems = u.items || {};
        let dailyTotalCost = 0;

        const series = Object.keys(ApiUsagePriorityMap)
          .map(usageKey => {
            const usage = dailyItems[usageKey as keyof ApiUsageItems] || 0;
            const pricePerUnit = pricingForDay.items?.[usageKey as keyof ApiUsageItems] || 0;
            const cost = usage * pricePerUnit;
            dailyTotalCost += cost;

            return {
              name: usageKey,
              value: this.isShowingCost ? cost : usage,
              usage,
              cost
            };
          })
          .sort((a, b) => ApiUsagePriorityMap[a.name] - ApiUsagePriorityMap[b.name]);

        return {
          id: dayStr,
          name: dayStr,
          totalUsage: Object.values(dailyItems).reduce((acc, val) => acc + (val || 0), 0),
          totalCost: dailyTotalCost,
          series
        };
      })
      .filter(item => item !== null);
  }

  calculateSummary(data: TransformedChartItem[]) {
    let totalUsage = 0;
    let totalCost = 0;
    const seriesTotals: Record<string, { totalValue: number; totalCost: number }> = {};

    data.forEach(day => {
      totalUsage += day.totalUsage;
      totalCost += day.totalCost;

      day.series.forEach(item => {
        if (!seriesTotals[item.name]) {
          seriesTotals[item.name] = { totalValue: 0, totalCost: 0 };
        }
        seriesTotals[item.name].totalValue += item.value;
        seriesTotals[item.name].totalCost += item.cost;
      });
    });

    this.summary = {
      totalUsage,
      totalCost,
      seriesSummary: Object.keys(seriesTotals)
        .map(key => ({
          name: key,
          totalValue: seriesTotals[key].totalValue,
          totalCost: seriesTotals[key].totalCost
        }))
        .sort((a, b) => ApiUsagePriorityMap[a.name] - ApiUsagePriorityMap[b.name]) // Ordenação correta
    };
  }

  getTooltipData(model: any): any[] {
    if (!model || model.length === 0) return [];
    const selectedDate = model[0]?.series || model.series;
    const dayData = this.resultsLineChartData.find(item => item.name === selectedDate);
    return dayData ? dayData.series : [];
  }

  getTotalCost(model: any): number {
    if (!model || model.length === 0) return 0;
    const selectedDate = model[0]?.series || model.series;
    const dayData = this.resultsLineChartData.find(item => item.name === selectedDate);
    return dayData ? Math.round(dayData.totalCost * 100) / 100 : 0;
  }

  getDate(model: any): string {
    if (!model || model.length === 0) return '';
    return model[0]?.series || model.series;
  }

  getNextDate(model: any): string {
    if (!model || model.length === 0) return '';

    const currentIndex = this.resultsLineChartData.findIndex(item => item.name === model.series);
    const currentData = this.resultsLineChartData[currentIndex]?.name;
    const nextDate = this.resultsLineChartData[currentIndex]?.lastNameGrouped;

    if (currentIndex === -1 || !nextDate || currentData === nextDate) {
      return '';
    }
    return nextDate;
  }

  formatXAxisTicks = (value: string): string => {
    const defaultFormat = this.i18nService.dateFormatAsMask() || null;
    const shortFormat = defaultFormat ? defaultFormat.replace(/\/?yyyy/, '') : null;
    return this.lDatePipe.transform(value, shortFormat) || '';
  };

  formatYAxisTicks = (value: string): string => {
    return this.isShowingCost ? this.currencyPipe.transform(value, this.currency) : value;
  };

  toggleChartData(): void {
    this.tickCounter = 0;
    this.resultsLineChartData = this.resultsLineChartData.map(day => ({
      ...day,
      series: day.series.map(item => ({
        ...item,
        value: this.isShowingCost ? item.cost : item.usage
      }))
    }));
  }

  getRealCost(totalCost: number): number {
    if (!totalCost) return 0;
    const limit = this.currencyCode === 'R$' ? this.COST_THRESHOLD_BRL : this.COST_THRESHOLD_USD;
    const difference = totalCost - limit;
    return difference > 0 ? difference : 0;
  }

  private groupChartData(data: TransformedChartItem[], startDate: dayjs.Dayjs, endDate: dayjs.Dayjs): TransformedChartItem[] {
    const totalDays = endDate.diff(startDate, 'day') + 1;

    // Verifica se o intervalo está dentro do mesmo mês e ano
    const isSameMonthAndYear = startDate.month() === endDate.month() && startDate.year() === endDate.year();

    // Se estiver no mesmo mês e ano, usa o número total de dias sem limite
    if (isSameMonthAndYear || totalDays <= 30) {
      return data;
    }

    // Caso contrário, limita o agrupamento a 30 blocos no máximo
    const maxBlocks = totalDays <= 30 ? totalDays : 30;

    // Ajusta o tamanho ideal do bloco
    const idealBlockSize = Math.ceil(totalDays / maxBlocks);
    let accumulatedDays = 0;
    let currentBlock: TransformedChartItem | null = null;
    let lastDateGrouped = ''; // Armazena o último nome do agrupamento
    const groupedData: TransformedChartItem[] = [];

    data.forEach((item, index) => {
      if (!currentBlock) {
        // Cria um novo bloco
        currentBlock = {
          id: item.id,
          name: item.name,
          totalUsage: 0,
          totalCost: 0,
          series: [],
          lastNameGrouped: '' // Inicializa o campo
        };
      }

      // Atualiza o último nome do agrupamento
      lastDateGrouped = item.name;

      // Acumula valores do item no bloco
      currentBlock.totalUsage += item.totalUsage;
      currentBlock.totalCost += item.totalCost;

      item.series.forEach(seriesItem => {
        const existingSeries = currentBlock.series.find(s => s.name === seriesItem.name);
        if (existingSeries) {
          existingSeries.usage += seriesItem.usage;
          existingSeries.value += seriesItem.usage;
          existingSeries.cost += seriesItem.cost;
        } else {
          currentBlock.series.push({ ...seriesItem });
        }
      });

      accumulatedDays++;

      // Fechar o bloco se atingirmos o tamanho ideal ou for o último item
      if (accumulatedDays >= idealBlockSize || index === data.length - 1) {
        currentBlock.lastNameGrouped = lastDateGrouped; // Salva o último nome agrupado
        groupedData.push(currentBlock);
        currentBlock = null;
        accumulatedDays = 0;
      }
    });

    return groupedData;
  }

  private fillMissingDatesOrCreateZeroData(data: TransformedChartItem[], startDate: dayjs.Dayjs, endDate: dayjs.Dayjs): TransformedChartItem[] {
    const allDates: Record<string, TransformedChartItem> = {};
    let currentDate = startDate.clone();
    const apiItems = Object.keys(ApiUsagePriorityMap);

    while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
      const dateKey = currentDate.format('YYYY-MM-DD');
      allDates[dateKey] = {
        id: dateKey,
        name: dateKey,
        totalUsage: 0,
        totalCost: 0,
        series: apiItems.map(name => ({
          name,
          value: 0,
          usage: 0,
          cost: 0
        }))
      };
      currentDate = currentDate.add(1, 'day');
    }

    // Se houver dados reais, sobrescreve os dias existentes
    data.forEach(item => {
      allDates[item.name] = item;
    });

    return Object.values(allDates);
  }
}
